import React, { useEffect, useRef, useState } from 'react'
import { useRetorikStore } from '../../Contexts/RetorikContext'
import { useActivityStore } from '../../Contexts/ActivityContext'

import { checkListDisplay } from '../../../utils/checkListDisplay'
import { processMarkdownAndSpeechMarkdown } from '../../../utils/markdownUtils'
import type { DisplayData } from '../../../models/types'
import type { RetorikActivity } from '../../../models/activityTypes'
import { TextFrom, DisplayDataTypes } from '../../../models/enums'

import Attachments from '../../Attachments/Attachments'
import TextBubble from './TextBubble'

interface DisplayStandardProps {
  currentQuestionId: string
  activities: Array<RetorikActivity>
  triggerScroll: () => void
  isWaitingForResponse: boolean
}

const DisplayStandard = ({ currentQuestionId, activities, triggerScroll, isWaitingForResponse }: DisplayStandardProps): JSX.Element => {
  const endedActivities = useActivityStore((state) => state.endedActivities)
  const watermark = useActivityStore((state) => state.watermark)
  const forceMobileView = useRetorikStore((state) => state.configuration.forceMobileView)

  const [displayData, setDisplayData] = useState<Array<DisplayData>>([])
  const [currentDisplayDataIndex, setCurrentDisplayDataIndex] = useState<number>(0)
  const [ended, setEnded] = useState<boolean>(!activities.length || endedActivities.includes(activities[activities.length - 1]?.id))

  const activitiesRef = useRef<Array<RetorikActivity>>([])
  const displayDataRef = useRef<Array<DisplayData>>([])

  useEffect(() => {
    setEnded(endedActivities.includes(activities[activities.length - 1]?.id))
    setDisplayData([])
    setCurrentDisplayDataIndex(0)
  }, [currentQuestionId])

  useEffect(() => {
    if (isWaitingForResponse) {
      // Create temporary activity with the id of the next one to prevent re-renders, in order to display waiting dots
      const nextActivityId = `${currentQuestionId.slice(0, currentQuestionId.length - watermark.toString().length)}${watermark.toString()}`

      const tempActivity: RetorikActivity = {
        id: nextActivityId,
        from: {
          id: 'fake-bot-id',
          role: 'bot'
        },
        type: 'message'
      }

      setDisplayData([
        {
          activity: tempActivity,
          type: DisplayDataTypes.text,
          isWaitingDots: true,
          canBeEnded: true,
          isEnded: false
        }
      ])
    }
  }, [isWaitingForResponse])

  useEffect(() => {
    activitiesRef.current = [...activities]
    if (activities.length) {
      let tempDisplayData: Array<DisplayData> = []
      let currentFirstStreamingActivity: RetorikActivity | undefined
      let streamingActivityHasAttachment = false
      let streamingTexts: Array<string> = []

      activities.forEach((activity, index) => {
        let splitTextDataToAdd: Array<DisplayData> = []
        // Check the attachments, special case if this is the message activity after streaming : show attachments if the first streaming activity didn't have any
        let attachments: Array<DisplayData> = []
        if (activity.attachments?.length) {
          if (activity.name?.toLowerCase() === 'davi.streammessageactivity') {
            streamingActivityHasAttachment = true
            attachments = [
              {
                type: DisplayDataTypes.attachment,
                activity: activity,
                canBeEnded: true,
                isEnded: true
              }
            ]
          } else if (!(activity.value?.streamingCount && streamingActivityHasAttachment) && !checkListDisplay(activity)) {
            attachments = [
              {
                type: DisplayDataTypes.attachment,
                activity: activity,
                canBeEnded: true,
                isEnded: true
              }
            ]
          }
        }

        // Deal with streaming activity first, then other activities except the complete streaming message
        if (activity.name?.toLowerCase() === 'davi.streammessageactivity') {
          // Set this streaming activity as first one if there is none
          !currentFirstStreamingActivity && (currentFirstStreamingActivity = activity)

          if (activity.text) {
            streamingTexts.push(activity.text)

            // If this is the last activity or if the next one isn't a streaming one, let's put the data in the array of data to display
            if (index === activities.length - 1 || activities[index + 1].name?.toLowerCase() !== 'davi.streammessageactivity') {
              splitTextDataToAdd = [
                {
                  type: DisplayDataTypes.text,
                  activity: {
                    ...currentFirstStreamingActivity,
                    htmlText: processMarkdownAndSpeechMarkdown(streamingTexts.join(''), false).htmlText
                  },
                  canBeEnded: false,
                  isEnded: false
                }
              ]
            }
          }
        } else if (activity.value?.streamingCount) {
          if (tempDisplayData.length) {
            const lastStreamingIndex = tempDisplayData.findLastIndex((d) => d.type === DisplayDataTypes.text)
            if (lastStreamingIndex !== -1) {
              // Set canBeEnded for the previous streaming
              tempDisplayData[lastStreamingIndex].canBeEnded = true
              // Set previous streaming ended if this activity is ended in speech synthesis
              tempDisplayData[lastStreamingIndex].isEnded = endedActivities.includes(activity.id)
            }
          }
          // Reset streaming data if the message containing all streaming data is received
          currentFirstStreamingActivity = undefined
          streamingActivityHasAttachment = false
          streamingTexts = []
        } else {
          let text = activity.htmlText
          if (!text && activity.text) {
            text = processMarkdownAndSpeechMarkdown(activity.text, false).htmlText
          }

          if (text) {
            activity.htmlText = text
            splitTextDataToAdd = [
              {
                type: DisplayDataTypes.text,
                activity: activity,
                canBeEnded: true,
                isEnded: endedActivities.includes(activity.id)
              }
            ]
          }
        }

        if (attachments.length || splitTextDataToAdd.length) {
          if (activity.messageType === 'welcome') {
            tempDisplayData = [...tempDisplayData, ...attachments, ...splitTextDataToAdd]
          } else {
            tempDisplayData = [...tempDisplayData, ...splitTextDataToAdd, ...attachments]
          }
        }
      })

      setDisplayData(tempDisplayData)
    }
  }, [currentQuestionId, activities.length, endedActivities])

  useEffect(() => {
    displayDataRef.current = [...displayData]
  }, [displayData])

  useEffect(() => {
    if (displayData.length && currentDisplayDataIndex <= displayData.length && !ended) {
      if (currentDisplayDataIndex === displayData.length) {
        setEnded(true)
      } else {
        if (displayData[currentDisplayDataIndex].type === DisplayDataTypes.attachment) {
          setCurrentDisplayDataIndex((current) => current + 1)
          !ended && triggerScroll()
        }
      }
    }
  }, [currentDisplayDataIndex, displayData, ended])

  const handleEnded = (): void => {
    setCurrentDisplayDataIndex((current) => current + 1)
    !ended && triggerScroll()
  }

  return (
    <React.Fragment>
      {displayData.map((data, key) => {
        if (key <= currentDisplayDataIndex || ended) {
          if (data.type === DisplayDataTypes.attachment) {
            return (
              data.activity.attachments?.length && (
                <div
                  key={`${data.activity.id}-${key}`}
                  className={`${forceMobileView ? 'rf-m-auto' : 'rf-w-full'} ${
                    data.activity.attachments.filter((att) => att.contentType.includes('card')).length === 1 && 'mobile:rf-px-4'
                  } rf-flex rf-flex-col vertical:rf-items-center rf-gap-2`}
                >
                  <Attachments activity={data.activity} triggerScroll={triggerScroll} />
                </div>
              )
            )
          } else if (data.type === DisplayDataTypes.text) {
            return (
              <TextBubble
                key={`${data.activity.id}-${key}`}
                from={TextFrom.bot}
                id={data.activity.id}
                htmlText={data.activity.htmlText}
                triggerScroll={triggerScroll}
                onEnded={handleEnded}
                canBeEnded={data.canBeEnded}
                isEnded={data.isEnded}
                isWaitingDots={data.isWaitingDots}
              />
            )
          }
        }

        return <React.Fragment key={`${data.activity.id}-${key}`} />
      })}
    </React.Fragment>
  )
}

export default DisplayStandard
